import {
  CreateHospitalEmailDomainInput,
  HospitalEmailDomain,
  HospitalSite,
  HospitalSiteListQuery,
  OhpHospitalDirectory,
  OhpHospitalSite,
  UpdateHospitalEmailDomainInput,
} from '../types/hospitalEmailDomain';
import { clerkAuthHeaders } from './clerkApiAuth';

const API_BASE_URL = '/api';

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.error === 'string' && body.error.trim()) {
      return body.error;
    }
  } catch {
    // ignore
  }
  return `${fallback} (${response.status})`;
}

async function parseJson<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    throw new Error(await readError(response, fallback));
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export async function fetchOhpHospitalDirectory(): Promise<OhpHospitalDirectory> {
  const response = await fetch(`${API_BASE_URL}/hospital-email-domains/ohp`, {
    signal: AbortSignal.timeout(8000),
  });
  const body = await parseJson<{ domains?: string[]; sites?: OhpHospitalSite[] }>(
    response,
    'Failed to load hospital domains'
  );
  return {
    domains: Array.isArray(body.domains) ? body.domains : [],
    sites: Array.isArray(body.sites) ? body.sites : [],
  };
}

export async function fetchOhpHospitalDomains(): Promise<string[]> {
  const directory = await fetchOhpHospitalDirectory();
  return directory.domains;
}

export async function fetchHospitalSites(query: HospitalSiteListQuery = {}): Promise<HospitalSite[]> {
  const params = new URLSearchParams();
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.classification) params.set('classification', query.classification);
  if (query.grantedAccessLevel) params.set('grantedAccessLevel', query.grantedAccessLevel);
  if (query.active) params.set('active', query.active);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE_URL}/admin/hospital-sites${suffix}`, {
    headers: await clerkAuthHeaders({ json: false }),
    signal: AbortSignal.timeout(15000),
  });
  return parseJson<HospitalSite[]>(response, 'Failed to load hospital sites');
}

export async function createHospitalSite(name: string): Promise<HospitalSite> {
  const response = await fetch(`${API_BASE_URL}/admin/hospital-sites`, {
    method: 'POST',
    headers: await clerkAuthHeaders(),
    body: JSON.stringify({ name }),
  });
  return parseJson<HospitalSite>(response, 'Failed to create hospital site');
}

export async function updateHospitalSite(id: string, name: string): Promise<HospitalSite> {
  const response = await fetch(`${API_BASE_URL}/admin/hospital-sites/${id}`, {
    method: 'PATCH',
    headers: await clerkAuthHeaders(),
    body: JSON.stringify({ name }),
  });
  return parseJson<HospitalSite>(response, 'Failed to update hospital site');
}

export async function deleteHospitalSite(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/admin/hospital-sites/${id}`, {
    method: 'DELETE',
    headers: await clerkAuthHeaders({ json: false }),
  });
  await parseJson<void>(response, 'Failed to delete hospital site');
}

export async function createHospitalEmailDomain(
  siteId: string,
  input: CreateHospitalEmailDomainInput
): Promise<HospitalEmailDomain> {
  const response = await fetch(`${API_BASE_URL}/admin/hospital-sites/${siteId}/domains`, {
    method: 'POST',
    headers: await clerkAuthHeaders(),
    body: JSON.stringify(input),
  });
  return parseJson<HospitalEmailDomain>(response, 'Failed to add email domain');
}

export async function updateHospitalEmailDomain(
  id: string,
  input: UpdateHospitalEmailDomainInput
): Promise<HospitalEmailDomain> {
  const response = await fetch(`${API_BASE_URL}/admin/hospital-email-domains/${id}`, {
    method: 'PATCH',
    headers: await clerkAuthHeaders(),
    body: JSON.stringify(input),
  });
  return parseJson<HospitalEmailDomain>(response, 'Failed to update email domain');
}

export async function deleteHospitalEmailDomain(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/admin/hospital-email-domains/${id}`, {
    method: 'DELETE',
    headers: await clerkAuthHeaders({ json: false }),
  });
  await parseJson<void>(response, 'Failed to delete email domain');
}
